import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Form, Select, Spin } from 'antd';
import { BlockOutlined } from '@ant-design/icons';
import Editor from '@kehila/react-page-editor';
import FormPlugin, { FormFieldPath } from './index';
import { cellPlugins } from '../plugins/cellPlugins';
import {t} from "@lingui/core/macro"
import {formEditorMiddleware} from './productInfoRebinding';
import logger from "../../../services/logger"

export default (formPlugin, namespace) => {
    const blocks = formPlugin.formData?.blocks || []

    return {
        Renderer: (props) => {

            const formFieldPath=useContext(FormFieldPath)

            if (props.remove) {
                formPlugin.onAddFormPlugin(props, props.nodeId, props.pluginConfig.id)
            }
            const [formBlock, setFormBlock] = useState(null)

            const {data} = props

            const blockId = data.blockId
            //console.log("blockId", blockId)


            // Race-safe : si blockId change rapidement, on ignore les réponses obsolètes.
            // Try/catch sur le fetch + sur la lecture JSON pour ne jamais crasher l'éditeur.
            useEffect(() => {
                if (!blockId) {
                    setFormBlock(null)
                    return
                }
                let cancelled = false
                ;(async () => {
                    try {
                        const request = await fetch(formPlugin.BASE_URL + "/forms/block/" + encodeURIComponent(blockId))
                        if (cancelled) return
                        if (!request.ok) {
                            logger.error({ status: request.status, blockId }, "[formBlock] HTTP error")
                            return
                        }
                        const response = await request.json()
                        if (cancelled) return
                        if (response?.data) {
                            setFormBlock(formPlugin.runtime?.prepareForm ? formPlugin.runtime.prepareForm(response.data) : response.data)
                        }
                    } catch (err) {
                        if (cancelled) return
                        logger.error({ err }, "[formBlock] fetch failed")
                    }
                })()
                return () => { cancelled = true }
            }, [blockId])


            if (!blockId) {
                if (!props.isPreviewMode && !props.readOnly) {
                    return (
                        <Alert showIcon type={"warning"} message={t`Veuillez choisir un bloc.`}/>
                    )
                }
                return null
            }

            //console.log(props)


            //console.log("formBlock",formBlock)
            if(!formBlock){
                return null
            }



            //console.log("formFieldPath",formFieldPath.path)

            // Scope de l'occurrence — clé du fix « form-block qui se reset ».
            // DEUX chemins, rôles disjoints :
            //  - occPath (relatif, = path) → uniquement la `key` React du sous-éditeur :
            //    unique par occurrence (sinon deux occurrences partagent la key →
            //    remontage → reset). Sa seule exigence est l'unicité.
            //  - occAbsolutePath (= absolutePath, INCLUT productId) → `valuesPath` :
            //    extractSubValues navigue dans formValues avec → DOIT être absolu,
            //    sinon les valeurs du block dans un produit ne sont pas trouvées.
            const occPath = Array.isArray(formFieldPath.path) ? formFieldPath.path : []
            const occAbsolutePath = Array.isArray(formFieldPath.absolutePath) ? formFieldPath.absolutePath : occPath
            // Chemin dans le store du Form Antd RACINE (un bloc imbriqué dans un
            // bloc repart du storePath du parent, jamais de son absolutePath relatif).
            const occStorePath = Array.isArray(formFieldPath.storePath) ? formFieldPath.storePath : occAbsolutePath

            const occScope = occAbsolutePath.join("/")
            const blockKey = occScope !== "" ? blockId + "@" + occScope : blockId

            return (
                <div>
                    <FormEditorRender
                        key={blockKey}
                        valuesPath={occAbsolutePath}
                        storePath={occStorePath}
                        relativePath={occPath}
                        baseFormPlugin={formPlugin}
                        form={formBlock}
                        lang={props.lang}
                    />
                </div>
            )
        },
        namespace: namespace,
        id: 'form-block' + namespace,
        title: t`Module de Formulaire`,
        description: t`Element de formulaire utilisable.`,
        icon: <BlockOutlined/>,
        iconColor: "#F39C12",
        version: 1,
        hideInMenu: formPlugin.formData.type !== "COMPLEX",
        controls: {
            type: 'custom',
            Component: Controls
        }
    }


    function Controls(props) {

        const {data, onChange} = props

        const [show, setShow] = useState(true)
        const refreshForm = () => {
            setShow(false)
            setTimeout(() => setShow(true), 100)
        }
        useEffect(() => {
            refreshForm()
        }, [props.lang, props.nodeId, props.device])

        if (!show) {
            return null
        }

        return (
            <div>
                <Form.Item label={"Block Form"}>
                    <Select placeholder={"Block"} value={data.blockId} onChange={(v) => onChange({blockId:v})}>
                        {blocks.map(i => (
                            <Select.Option key={i} value={i.id}>{i.name}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            </div>
        )
    }
}





// Parse JSON safe : retourne null si invalide au lieu de crasher
function safeParseJSON(str, label) {
    if (!str) return null
    try {
        return JSON.parse(str)
    } catch (e) {
        logger.error({ label, err: e }, "[formBlock] JSON.parse failed for")
        return null
    }
}

/**
 * Extrait les valeurs du sous-formulaire (ce block) depuis l'objet de valeurs
 * du formulaire racine, en suivant `valuesPath` (les segments de namespace posés
 * par les containers parents : productInfo, contact, child…). Défensif : tout
 * segment manquant → undefined sans throw.
 */
function extractSubValues(rootValues, valuesPath) {
    if (!rootValues) return undefined
    if (!Array.isArray(valuesPath) || valuesPath.length === 0) return rootValues
    return valuesPath.reduce((acc, key) => (acc == null ? acc : acc[key]), rootValues)
}

const FormEditorRender = ({ form, lang, onChange, baseFormPlugin, valuesPath, storePath, relativePath }) => {
    const [loading, setLoading] = useState(true);
    const [plugins, setPlugins] = useState([]);
    // Compteur de re-render : permet aux conditions internes du block (checkConditions
    // → forceUpdate) de re-rendre l'Editor SANS changer la prop `value` (qui reste
    // figée → pas de reset du store React-Page).
    const [, setRenderTick] = useState(0);

    const formPluginRef = useRef(null);

    // valuesPath et form lus dans des callbacks/effets : gardés en ref pour capturer
    // la dernière valeur sans recréer le plugin interne ni se réabonner.
    const valuesPathRef = useRef(valuesPath);
    valuesPathRef.current = valuesPath;
    const formRef = useRef(form);
    formRef.current = form;

    const forceUpdate = () => setRenderTick((x) => x + 1);

    // ── Init du FormPlugin interne : UNE SEULE FOIS.
    // La key (unique PAR OCCURRENCE, posée par le Renderer parent : blockId@path/index)
    // garantit un remount propre quand l'occurrence change. On ne dépend donc PAS de
    // [form] ici, pour ne pas recréer l'Editor (et vider les champs) à chaque nouvelle
    // référence de `form`.
    useEffect(() => {
        let cancelled = false

        const initForm = () => {
            const content = safeParseJSON(formRef.current?.content, "form.content (init)")

            const plugin = new FormPlugin(null, formRef.current, content, null, true);
            plugin.setRuntime(baseFormPlugin.runtime);
            const pluginList = plugin.getPlugins(cellPlugins);
            plugin.setOrg(baseFormPlugin.org);
            plugin.setEditor({ forceUpdate: forceUpdate })
            // Lien vers le plugin RACINE (celui qui porte formValues + le Form) :
            // permet à un bloc imbriqué de resynchroniser le moteur après une
            // écriture programmatique (syncFromForm). Bloc dans bloc → toujours
            // la vraie racine.
            plugin.rootFormPlugin = baseFormPlugin.rootFormPlugin || baseFormPlugin

            // Ré-hydratation immédiate : injecter les valeurs déjà présentes dans le
            // form racine avant le 1er rendu (pas de fenêtre vide).
            try {
                const subValues = extractSubValues(baseFormPlugin.formValues, valuesPathRef.current)
                if (subValues !== undefined) {
                    plugin.setFormValues(subValues)
                    plugin.checkConditions()
                }
            } catch (e) {
                logger.error({ err: e }, "[formBlock] initial hydration failed")
            }

            if (cancelled) return
            formPluginRef.current = plugin;
            setPlugins(pluginList);
            setLoading(false);
        };

        initForm();

        // eslint-disable-next-line react-hooks/exhaustive-deps
        return () => { cancelled = true }
    }, []);

    // ── Abonnement aux changements de valeurs du form racine. Handler stable →
    // cleanup désabonne réellement. checkConditions() ne re-rend (via forceUpdate)
    // que si une condition CHANGE réellement.
    useEffect(() => {
        const handleBaseValuesChange = (e) => {
            const plugin = formPluginRef.current
            if (!plugin) return
            try {
                const subValues = extractSubValues(e?.detail, valuesPathRef.current)
                plugin.setFormValues(subValues)
                plugin.checkConditions()
            } catch (err) {
                logger.error({ err }, "[formBlock] values sync failed")
            }
        }

        baseFormPlugin.on("formValuesChanges", handleBaseValuesChange)
        return () => baseFormPlugin.off("formValuesChanges", handleBaseValuesChange)
    }, [baseFormPlugin]);

    const handleChange = (v) => {
        const plugin = formPluginRef.current;
        if (!plugin) return;
        plugin.setTree(v);
        plugin.checkFormPlugins();
        // On NE réinjecte PAS de nouvelle `value` : React-Page gère son store en
        // interne (uncontrolled). Repasser une `value` différente déclencherait
        // updateValue → RESET du store → perte des champs.
        onChange?.(v);
    };

    // ── Valeur FIGÉE de l'Editor interne.
    // React-Page (EditorStoreProvider) est CONTROLLED : il RESET tout son store dès
    // que la prop `value` change de référence (deepEquals). On la calcule donc UNE
    // SEULE FOIS et on ne la change jamais → l'éditeur devient uncontrolled, gère ses
    // saisies en interne, et ne se vide plus. Les conditions internes re-rendent via
    // forceUpdate (re-render sans changer `value`).
    const frozenValueRef = useRef(null);
    if (frozenValueRef.current === null && formPluginRef.current) {
        const content = safeParseJSON(formRef.current?.content, "form.content (frozen)")
        frozenValueRef.current = formPluginRef.current.hookValueEditor(content);
    }
    const editorValue = frozenValueRef.current;

    return (
        <div style={{ width: '100%' }}>
            {loading ? (
                <Spin spinning={true} />
            ) : (
                // Réinjection du contexte de chemin pour les champs internes du block.
                // ⚠️ Le block a son PROPRE FormPlugin dont le store (formValues) est
                // DÉJÀ l'occurrence extraite (subValues).
                //  - `path` = relativePath → names Antd des feuilles internes
                //    (comportement historique préservé).
                //  - `absolutePath` = [] → le sous-store EST sa propre racine pour la
                //    navigation de valeurs. Surtout PAS relativePath : quand le block
                //    est dans une occurrence produit, relativePath contient l'index
                //    externe (ex [0]) qui n'existe PAS dans subValues → getScopeChain
                //    casserait dessus et une condition interne (ex dans un contact du
                //    block) ne trouverait jamais son champ → masquage erroné.
                //    (Correctif porté depuis l'admin, commit 9fc2a70.)
                //  - `storePath` = chemin absolu dans le store du Form RACINE, identique
                //    des deux côtés (web/admin).
                <FormFieldPath.Provider value={{ path: relativePath || [], absolutePath: [], storePath: storePath || valuesPath || [], listFieldProps: null }}>
                    <Editor
                        middleware={formEditorMiddleware}
                        cellPlugins={plugins}
                        value={editorValue}
                        onChange={handleChange}
                        cellSpacing={{ x: 50, y: 0 }}
                        readOnly
                        lang={lang}
                    />
                </FormFieldPath.Provider>
            )}
        </div>
    );
};

